import React, { useState, useMemo } from 'react';
import classNames from 'classnames';

interface LogRecord {
  timestamp: string;
  start_timestamp: string;
  trace_id: string; // extracted from TRACE column
  trace_raw: any;
  resource_attributes: any;
  record_type: string;
  record: any;
  record_attributes: any;
  value: any;
  // raw fields for debugging or full view
  raw: { [key: string]: string };
}

interface TraceGroup {
  traceId: string;
  startTime: string;
  records: LogRecord[];
  userQuery?: string;
  status: 'SUCCESS' | 'ERROR';
  duration: number;
}

// Helper to recursively parse JSON strings
const deepParseJson = (data: any): any => {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === 'object' && parsed !== null) {
        return deepParseJson(parsed);
      }
      return data;
    } catch {
      return data;
    }
  }

  if (Array.isArray(data)) {
    return data.map(deepParseJson);
  }

  if (typeof data === 'object' && data !== null) {
    const newObj: any = {};
    Object.keys(data).forEach(key => {
      newObj[key] = deepParseJson(data[key]);
    });
    return newObj;
  }

  return data;
};

// Component to render Agent Tables
const AgentTable: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.tables?.labels || !data?.tables?.data) return null;

  const labels = data.tables.labels;
  const rows = data.tables.data;
  const columns = Object.keys(labels);

  return (
    <div className="overflow-x-auto border rounded-lg shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-[#f66011]/10">
          <tr>
            {columns.map(key => (
              <th key={key} className="px-4 py-3 text-left font-bold text-[#f66011] uppercase tracking-wider">
                {labels[key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row: any, idx: number) => (
            <tr key={idx} className="hover:bg-gray-50 transition-colors">
              {columns.map(key => (
                <td key={key} className="px-4 py-2 text-gray-700 whitespace-nowrap">
                  {typeof row[key] === 'number'
                    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row[key])
                    : String(row[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-2 text-xs text-gray-500 bg-gray-50 text-right">
        {rows.length} rows
      </div>
    </div>
  );
};

const LogAnalyzer: React.FC = () => {
  const [logs, setLogs] = useState<LogRecord[]>([]);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  // error state is used but not shown in this snippet context, assuming it exists
  const [error, setError] = useState<string | null>(null);
  const [viewingContent, setViewingContent] = useState<{ title: string; content: any } | null>(null);
  const [wrapText, setWrapText] = useState(false);

  // ... (rest of the component)


  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'SUCCESS' | 'ERROR'>('ALL');

  const parseCSV = (text: string): LogRecord[] => {
    const lines: string[][] = [];
    let currentLine: string[] = [];
    let currentField = '';
    let insideQuotes = false;

    // Custom CSV parser to handle multiline quoted fields
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i++; // Skip next quote
        } else {
          // Toggle quotes
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // End of field
        currentLine.push(currentField);
        currentField = '';
      } else if ((char === '\r' || char === '\n') && !insideQuotes) {
        // End of line
        // Handle CRLF
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
        if (currentField || currentLine.length > 0) {
          currentLine.push(currentField);
          lines.push(currentLine);
        }
        currentLine = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }
    // Push last field/line if exists
    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField);
      lines.push(currentLine);
    }

    if (lines.length < 2) return [];

    const headers = lines[0].map(h => h.trim());

    // Helper to safely parse JSON
    const safeJsonParse = (str: string) => {
      if (!str) return null;
      try {
        return JSON.parse(str);
      } catch (e) {
        return str;
      }
    };

    const parsedRecords: LogRecord[] = lines.slice(1).map(line => {
      const record: any = {};
      headers.forEach((header, index) => {
        record[header] = line[index];
      });

      // Extract specific fields
      const traceRaw = safeJsonParse(record['TRACE']);
      // Fallback strategies for input_id
      const recordAttributes = safeJsonParse(record['RECORD_ATTRIBUTES']);
      const resourceAttributes = safeJsonParse(record['RESOURCE_ATTRIBUTES']);

      const inputId = recordAttributes?.['ai.observability.input_id'] ||
        resourceAttributes?.['ai.observability.input_id'] ||
        recordAttributes?.['ai.observability.record_id'] ||
        'unknown';

      return {
        timestamp: record['TIMESTAMP'],
        start_timestamp: record['START_TIMESTAMP'],
        trace_id: inputId, // Using input_id as the main grouping key now
        trace_raw: traceRaw,
        resource_attributes: resourceAttributes,
        record_type: record['RECORD_TYPE'],
        record: safeJsonParse(record['RECORD']),
        record_attributes: recordAttributes,
        value: safeJsonParse(record['VALUE']),
        raw: record
      };
    });

    return parsedRecords;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParsing(true);
    setError(null);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = parseCSV(text);

        // Sort by timestamp
        parsed.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        setLogs(parsed);
      } catch (err) {
        console.error(err);
        setError('Failed to parse CSV file.');
      } finally {
        setParsing(false);
      }
    };

    reader.readAsText(file);
  };

  const traceGroups = useMemo(() => {
    const groups: { [key: string]: TraceGroup } = {};

    logs.forEach(log => {
      if (!groups[log.trace_id]) {
        groups[log.trace_id] = {
          traceId: log.trace_id,
          startTime: log.timestamp,
          records: [],
          status: 'SUCCESS',
          duration: 0
        };
      }
      groups[log.trace_id].records.push(log);
    });

    Object.values(groups).forEach(group => {
      // 1. Extract User Query
      const queryRecord = group.records.find(r =>
        r.record_type === 'EVENT' &&
        r.value?.['snow.ai.observability.request_body']?.messages?.[0]?.content?.[0]?.text
      );
      if (queryRecord) {
        group.userQuery = queryRecord.value['snow.ai.observability.request_body'].messages[0].content[0].text;
      }

      // 2. Determine Status
      // Priority: Check specific agent status field in record_attributes
      const agentStatusRecord = group.records.find(r =>
        r.record_attributes?.['snow.ai.observability.agent.status']
      );

      if (agentStatusRecord) {
        const status = agentStatusRecord.record_attributes['snow.ai.observability.agent.status'];
        group.status = status === 'SUCCESS' ? 'SUCCESS' : 'ERROR';
      } else {
        // Fallback heuristic: look for "ERROR" record type or status code != 'OK'/'200'
        const hasError = group.records.some(r =>
          r.record_type === 'ERROR' ||
          (r.record?.status?.code && String(r.record.status.code) !== 'OK' && String(r.record.status.code) !== '200')
        );
        group.status = hasError ? 'ERROR' : 'SUCCESS';
      }

      // 3. Calculate Duration
      if (group.records.length > 1) {
        const start = new Date(group.records[0].timestamp).getTime();
        const end = new Date(group.records[group.records.length - 1].timestamp).getTime();
        group.duration = end - start;
      }
    });

    // Convert to array and sort by start time
    return Object.values(groups).sort((a, b) =>
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  }, [logs]);

  const filteredTraceGroups = useMemo(() => {
    return traceGroups.filter(group => {
      const matchesText =
        group.traceId.toLowerCase().includes(filterText.toLowerCase()) ||
        group.userQuery?.toLowerCase().includes(filterText.toLowerCase());

      const matchesStatus = filterStatus === 'ALL' || group.status === filterStatus;

      return matchesText && matchesStatus;
    });
  }, [traceGroups, filterText, filterStatus]);

  const metrics = useMemo(() => {
    const totalTraces = traceGroups.length;
    const errorTraces = traceGroups.filter(g => g.status === 'ERROR').length;
    const errorRate = totalTraces > 0 ? ((errorTraces / totalTraces) * 100).toFixed(1) : '0.0';
    const avgLatency = totalTraces > 0
      ? (traceGroups.reduce((acc, g) => acc + g.duration, 0) / totalTraces).toFixed(0)
      : '0';

    return { totalTraces, errorTraces, errorRate, avgLatency };
  }, [traceGroups]);

  const selectedTrace = useMemo(() => {
    if (!selectedTraceId) return null;
    return traceGroups.find(g => g.traceId === selectedTraceId);
  }, [selectedTraceId, traceGroups]);

  return (
    <div className="flex h-screen w-full bg-gray-50 relative">
      {/* Sidebar - Trace List */}
      <div className="w-80 flex flex-col border-r bg-white h-full">
        {/* Metrics Dashboard */}
        {logs.length > 0 && (
          <div className="bg-gray-100 p-3 flex justify-around text-center text-xs border-b">
            <div>
              <div className="font-bold text-gray-700">{metrics.totalTraces}</div>
              <div className="text-gray-500">Total</div>
            </div>
            <div>
              <div className="font-bold text-gray-700">{metrics.avgLatency}ms</div>
              <div className="text-gray-500">Avg Lat</div>
            </div>
            <div>
              <div className={classNames("font-bold", metrics.errorTraces > 0 ? "text-red-600" : "text-green-600")}>
                {metrics.errorRate}%
              </div>
              <div className="text-gray-500">Errors</div>
            </div>
          </div>
        )}

        <div className="p-4 border-b">
          <h2 className="text-lg font-bold mb-4">Log Analyzer</h2>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full text-xs text-gray-500
              file:mr-2 file:py-1 file:px-2
              file:rounded-full file:border-0
              file:text-xs file:font-semibold
              file:bg-[#f66011]/10 file:text-[#f66011]
              hover:file:bg-[#f66011]/20 mb-3"
          />

          {/* Search & Filter */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search ID or Query..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="flex-1 w-full text-xs border rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-[#f66011]"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="text-xs border rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-[#f66011]"
            >
              <option value="ALL">All</option>
              <option value="SUCCESS">Success</option>
              <option value="ERROR">Error</option>
            </select>
          </div>

          {parsing && <p className="text-sm text-gray-500 mt-2">Parsing...</p>}
          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredTraceGroups.map(group => (
            <div
              key={group.traceId}
              onClick={() => setSelectedTraceId(group.traceId)}
              className={classNames(
                "p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors border-l-4",
                {
                  "bg-[#f66011]/10 border-l-[#f66011]": selectedTraceId === group.traceId,
                  "border-l-transparent": selectedTraceId !== group.traceId && group.status === 'SUCCESS',
                  "border-l-red-500 bg-red-50": group.status === 'ERROR', // Highlight errors
                  "border-l-green-500": selectedTraceId !== group.traceId && group.status === 'SUCCESS' // Subtle green for success? Optional.
                }
              )}
            >
              <div className="flex justify-between items-start">
                <div className="font-mono text-xs text-gray-500 truncate flex-1" title={group.traceId}>
                  {group.traceId}
                </div>
                {group.status === 'ERROR' && (
                  <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded font-bold ml-2">ERR</span>
                )}
              </div>

              {group.userQuery && (
                <div className="text-sm font-medium text-gray-800 mt-1 line-clamp-2" title={group.userQuery}>
                  {group.userQuery}
                </div>
              )}
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">
                  {new Date(group.startTime).toLocaleTimeString()}
                </span>
                <span className="text-xs font-mono text-gray-400">
                  {group.duration}ms
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {group.records.length} logs
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content - Trace Flow */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {selectedTrace ? (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-6 flex justify-between items-end">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Trace Analysis</h1>
                {selectedTrace?.userQuery && (
                  <p className="text-md text-gray-700 mt-1 font-medium">{selectedTrace.userQuery}</p>
                )}
                <p className="font-mono text-gray-500 mt-1 text-sm">{selectedTrace?.traceId}</p>
              </div>
              <div className="text-right">
                <div className="mb-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(selectedTrace, null, 2));
                      alert('Trace JSON copied to clipboard!');
                    }}
                    className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors"
                  >
                    Copy Trace JSON
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Started: {new Date(selectedTrace?.startTime || '').toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">
                  Duration: {selectedTrace.duration}ms
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {selectedTrace?.records?.map((record, idx) => {
                // Calculate relative timing for timeline
                const traceDuration = selectedTrace.duration || 1;
                const recordTime = new Date(record.timestamp).getTime();
                const traceStart = new Date(selectedTrace.startTime).getTime();
                const relativeStart = recordTime - traceStart;
                const offsetPercent = (relativeStart / traceDuration) * 100;

                return (
                  <div key={idx} className="relative">
                    {/* Timeline Connector Line (vertical) */}
                    {idx < selectedTrace.records.length - 1 && (
                      <div className="absolute left-6 top-8 bottom-[-16px] w-0.5 bg-gray-200 -z-10"></div>
                    )}

                    <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow relative">
                      {/* Visual Timeline Marker on Top Border */}
                      <div
                        className="absolute top-0 h-1 bg-gray-200 rounded-t-lg w-full overflow-hidden"
                        title={`Offset: ${relativeStart}ms`}
                      >
                        <div
                          className="h-full bg-[#f66011]"
                          style={{
                            width: '10px',
                            marginLeft: `${Math.min(offsetPercent, 98)}%`
                          }}
                        />
                      </div>

                      <div className="flex justify-between items-start mb-2 mt-2">
                        <div className="flex items-center gap-2">
                          <span className={classNames(
                            "px-2 py-0.5 rounded text-xs font-bold uppercase",
                            {
                              "bg-[#f66011]/10 text-[#f66011]": record.record_type === 'EVENT',
                              "bg-purple-100 text-purple-800": record.record_type === 'SPAN',
                              "bg-green-100 text-green-800": !['EVENT', 'SPAN'].includes(record.record_type)
                            }
                          )}>
                            {record.record_type}
                          </span>
                          <span className="font-mono text-xs text-gray-500">
                            {new Date(record.timestamp).toLocaleTimeString()}.{new Date(record.timestamp).getMilliseconds()}
                          </span>
                        </div>
                      </div>

                      {/* Record Name / Status */}
                      {record.record && (
                        <div className="mb-2">
                          {record.record.name && <h4 className="font-semibold text-gray-800">{record.record.name}</h4>}
                          {record.record.status && (
                            <div className="text-xs text-gray-600 mt-1">
                              Status: {record.record.status.code}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Attributes */}
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        {record.record_attributes && Object.keys(record.record_attributes).length > 0 && (
                          <div className="bg-gray-50 p-2 rounded text-xs font-mono relative group">
                            <div className="flex justify-between items-center mb-1 sticky top-0 bg-gray-50">
                              <span className="font-bold text-gray-500">Attributes</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setViewingContent({ title: 'Record Attributes', content: record.record_attributes }); }}
                                className="opacity-0 group-hover:opacity-100 text-[#f66011] hover:text-[#d0500e] transition-opacity"
                                title="View Full Screen"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                              </button>
                            </div>
                            <div className="overflow-auto max-h-40">
                              <pre>{JSON.stringify(record.record_attributes, null, 2)}</pre>
                            </div>
                          </div>
                        )}

                        {record.value && (
                          <div className="bg-gray-50 p-2 rounded text-xs font-mono relative group">
                            <div className="flex justify-between items-center mb-1 sticky top-0 bg-gray-50">
                              <span className="font-bold text-gray-500">Value</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setViewingContent({ title: 'Record Value', content: record.value }); }}
                                className="opacity-0 group-hover:opacity-100 text-[#f66011] hover:text-[#d0500e] transition-opacity"
                                title="View Full Screen"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                              </button>
                            </div>
                            <div className="overflow-auto max-h-40">
                              <pre>{JSON.stringify(record.value, null, 2)}</pre>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Collapsible raw data */}
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Show Full Raw JSON</summary>
                        <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded mt-2 overflow-auto">
                          {JSON.stringify(record.raw, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p>Select a trace to view details</p>
            </div>
          </div>
        )}
      </div>




      {/* Full Screen Modal */}
      {viewingContent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingContent(null)}>
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-800">{viewingContent?.title}</h3>
              <div className="flex items-center gap-4">
                <label className="flex items-center cursor-pointer select-none text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={wrapText}
                    onChange={(e) => setWrapText(e.target.checked)}
                    className="mr-2"
                  />
                  Wrap Text
                </label>
                <button
                  onClick={() => setViewingContent(null)}
                  className="text-gray-500 hover:text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-full p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto flex-1 bg-gray-900 text-gray-100">
              {(() => {
                const parsedContent = deepParseJson(viewingContent?.content);

                // Check if it's an Agent Response with a table
                // Structure: snow.ai.observability.agent.response (stringified) -> { tables: { labels: ..., data: ... } }
                // OR if we are viewing the response directly

                // Case 1: We are viewing record_attributes and it has the agent response
                if (parsedContent?.['snow.ai.observability.agent.response']?.tables) {
                  return (
                    <div className="bg-white p-4 rounded text-black font-sans">
                      <h4 className="font-bold mb-2 text-lg">Agent Table Response</h4>
                      <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded">
                        {parsedContent['snow.ai.observability.agent.response'].message}
                      </div>
                      <AgentTable data={parsedContent['snow.ai.observability.agent.response']} />

                      <h4 className="font-bold mt-8 mb-2">Full JSON Source</h4>
                      <pre className={classNames("text-xs bg-gray-800 text-green-400 p-2 rounded", { "whitespace-pre-wrap": wrapText })}>
                        {JSON.stringify(parsedContent, null, 2)}
                      </pre>
                    </div>
                  );
                }

                // Case 2: We are viewing the 'agent.response' object directly (if passed specifically)
                if (parsedContent?.tables?.labels && parsedContent?.tables?.data) {
                  return (
                    <div className="bg-white p-4 rounded text-black font-sans">
                      <h4 className="font-bold mb-2 text-lg">Agent Table Response</h4>
                      {parsedContent.message && (
                        <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded">
                          {parsedContent.message}
                        </div>
                      )}
                      <AgentTable data={parsedContent} />

                      <h4 className="font-bold mt-8 mb-2">Full JSON Source</h4>
                      <pre className={classNames("text-xs bg-gray-800 text-green-400 p-2 rounded", { "whitespace-pre-wrap": wrapText })}>
                        {JSON.stringify(parsedContent, null, 2)}
                      </pre>
                    </div>
                  );
                }

                // Default: Enhanced JSON View
                return (
                  <pre className={classNames("text-sm font-mono text-green-400", { "whitespace-pre-wrap": wrapText })}>
                    {JSON.stringify(parsedContent, null, 2)}
                  </pre>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogAnalyzer;
